import React              from 'react';

import Table              from 'react-bootstrap/Table';
import InfiniteScroll     from 'react-infinite-scroll-component';

import Alert              from '../utils/Alert';
import getApiPromise      from '../utils/getApiPromise';
import * as MenuItem      from '../components/MenuItem';
import { Row } from 'react-bootstrap';

export type Row = Record<string,any>;

export interface Props {
    sortPropName?      : string;
    sortDirection?     : (1|-1);
}

export interface State<T extends Record<string,any>=Row> {
    sortPropName       : string;
    sortDirection      : (1|-1);
    initialLoad        : boolean;
    hasMore            : boolean;
    rows               : T[];
    correctPageLoaded? : number;
}

export abstract class InfiniteTable<
    T extends Record<string,any> = Row,
    MProps extends Props         = Props,
    MState extends State<T>      = State<T>
> extends React.Component<MProps,MState> {

    private pageSize     : number = 50;
    private queryingNow  : boolean = false;
    private isMounted_   : boolean = false;
    public  alert        : Alert = new Alert();

    protected abstract fetchRows( orderby:string, pageSize:number, skip:number ) : Promise<T[]>;
    protected abstract renderThead() : any;
    protected abstract renderRow( r:T ) : any;
    protected          renderTfoot( colSpan:number ) {
        if( this.state.hasMore )
            return null;
        return (
            <tr key="-2">
                <td colSpan={colSpan} className="text-center"><strong>Total rows</strong>: {(this.state.rows||[]).length}</td>
            </tr>
        );
    }

    constructor( props:MProps ) {
        super(props);
        this.pageSize   = 50;
        // @ts-expect-error
        this.state = {
            sortPropName  : (props.sortPropName || '_id'),
            sortDirection : (props.sortDirection || 1),
            initialLoad   : true,
            hasMore       : true,
            rows          : []
        };
    }
    //
    getTablePromise<P=T[]>( ...api_promise_args:any[] ) : Promise<P> {
        this.queryingNow = true;
        // @ts-expect-error TS2556
        return getApiPromise<P>(...api_promise_args)
            .then( table => {
                if( !table || table.err )
                    throw Error(table.err||'table is empty');
                return table;
            })
            .catch( err => {
                this.alert.set(`server api failed (${err.message})`);
                return [];
            })
            .finally(() => {
                this.queryingNow = false;
            });
    }
    getSortingIcon( propName:string ) {
        if( this.state.sortPropName!==propName )
            return '';
        if( this.state.sortDirection===1 )
            return <i className="fa fa-angle-double-up">&nbsp;</i>;
        return <i className="fa fa-angle-double-down">&nbsp;</i>;
    }
    static getObjectProp( r:Record<string,any>, parts:string[], ndx=0 ) : any {
        if( ndx>=parts.length )
            return r;
        if( typeof r !== 'object' )
            return r;
        r = r[parts[ndx]];
        return this.getObjectProp(r,parts,ndx+1);
    };
    // infinite pagination interface
    fetchPage = () => {
        const orderby = this.state.sortPropName+((this.state.sortDirection>0)?' DESC':' ASC');
        return this.fetchRows(orderby,this.pageSize,this.state.rows.length).then( new_rows => {
            if( !this.isMounted_ )
                return []; // the component could have unmounted by the time the data returned
            this.state.rows.push(...(new_rows||[]));
            return this.setState({
                initialLoad : false,
                hasMore     : (new_rows?.length>=this.pageSize),
                rows        : this.state.rows
            });
        });
    }
    sort( propName:string ) {
        const sortDirection = ((this.state.sortPropName===propName) ? (0-this.state.sortDirection) : -1) as (1|-1);
        if( this.state.hasMore ) {
            // Has to grab rows from the server
            const orderby = propName+((sortDirection>0)?' DESC':' ASC');
            return this.fetchRows(orderby,this.state.rows.length,0).then( rows => {
                if( !this.isMounted_ )
                    return []; // the component could have unmounted by the time the data returned
                return this.setState({
                    hasMore        : (rows.length>=this.state.rows.length),
                    rows           : rows,
                    sortPropName : propName,
                    sortDirection : sortDirection
                });
            });
        }
        else {
            // All the sorting can happen on the client
            const props       = propName.split('.');
            const sorter = ( t1:T, t2:T ) => {
                const v1 = InfiniteTable.getObjectProp(t1,props);
                const v2 = InfiniteTable.getObjectProp(t2,props);
                return sortDirection*((v1<v2) ? 1 : ((v1>v2) ? -1 : 0));
            };
            return this.setState({
                rows           : this.state.rows.sort(sorter),
                sortPropName   : propName,
                sortDirection  : sortDirection
            });
        }
    }
    // React component methods
    componentDidMount() {
        this.isMounted_ = true;
    }
    componentWillUnmount() {
        this.isMounted_ = false;
    }
    render() {
        const thead      = this.renderThead();
        let colSpan = thead.props.children.length;
        const rows  = (this.state.rows||[]).map( r => {
            const rendering =  this.renderRow(r);
            if( typeof rendering.props.children?.length === 'number' )
                colSpan = Math.max(colSpan,rendering.props.children.length);
            return rendering;
        });
        const tfoot      = this.renderTfoot(colSpan);
        return MenuItem.withAlert( alert => {
            this.alert = alert;
            return (
                <div className="content">
                    <div className="container-fluid">
                        <div className="wrapper">
                            <div className="card-body section-card table-theme">
                                <InfiniteScroll
                                    dataLength  = {rows.length}
                                    next        = {this.fetchPage}
                                    hasMore     = {this.state.hasMore}
                                    loader      = {<table><tbody>
                                        <tr key="-1" className="loader"><td colSpan={colSpan} className="text-center">Loading ...</td></tr>
                                    </tbody></table>}>
                                    <Table responsive hover size="sm">
                                        <thead>
                                            {thead}
                                        </thead>
                                        <tbody>
                                        {
                                            rows
                                        }
                                        </tbody>
                                        <tfoot>
                                            {tfoot}
                                        </tfoot>
                                    </Table>
                                </InfiniteScroll>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    }
}

export default InfiniteTable;
